if (!window.Eurus.loadedScript.has('slide-vertical.js')) {
  window.Eurus.loadedScript.add('slide-vertical.js');

  requestAnimationFrame(() => {
    document.addEventListener("alpine:init", () => {
      Alpine.data('xSlideVertical', () => ({
        slideIndex: '',
        slideOnMouseDown: false,
        slideOfset: 0,
        scrollTopBody: 0,
        isFullyInView: false,
        scrollHijacked: false,
        currentSlideIndex: 0,
        totalSlides: 0,
        slideshowElement: null,
        splideInstance: null,
        accumulatedDelta: 0,
        isScrollingSlides: false,
        isMobile: false,
        dragEnabled: false,

        init() {
          this.isMobile = window.innerWidth < 768;

          // Update mobile status on resize
          window.addEventListener('resize', () => {
            this.isMobile = window.innerWidth < 768;
          });

          // Wait for splide to initialize
          this.$nextTick(() => {
            this.slideshowElement = this.$el;

            // Get splide instance after it's loaded
            const initSplide = () => {
              this.splideInstance = this.$el.querySelector('.splide')?.splide;
              if (this.splideInstance) {
                this.totalSlides = this.splideInstance.length;
                this.currentSlideIndex = this.splideInstance.index;

                // Listen to slide changes - use both 'move' and 'moved' for reliability
                this.splideInstance.on('move', (newIndex) => {
                  this.currentSlideIndex = newIndex;
                });

                this.splideInstance.on('moved', (newIndex) => {
                  this.currentSlideIndex = newIndex;

                  // Update slide index for existing functionality
                  if (newIndex === 0) {
                    this.slideIndex = 'first';
                  } else if (newIndex === this.totalSlides - 1) {
                    this.slideIndex = 'last';
                  } else {
                    this.slideIndex = 'middle';
                  }
                });
              } else {
                // Retry if splide not ready yet
                setTimeout(initSplide, 200);
              }
            };
            setTimeout(initSplide, 500);

            // Only enable scroll hijacking and overlay on mobile
            if (this.isMobile) {
              this.setupScrollBlockingOverlay();
              this.setupScrollHijacking();
            }
          });
        },

        setupScrollBlockingOverlay() {
          // Create transparent overlay to block interaction until fully in view
          const overlay = document.createElement('div');
          overlay.id = 'slideshow-scroll-overlay';
          overlay.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: transparent;
            z-index: 10;
            pointer-events: auto;
            transition: opacity 0.3s ease;
          `;

          this.slideshowElement.style.position = 'relative';
          this.slideshowElement.appendChild(overlay);
          this.overlayElement = overlay;
        },

        setupScrollHijacking() {
          // Intersection Observer to detect when slideshow is fully in view
          const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              // Check if slideshow is fully visible (95% in viewport)
              this.isFullyInView = entry.intersectionRatio >= 0.95;

              if (this.isFullyInView) {
                // Fully in view - remove overlay, enable interactions
                if (this.overlayElement) {
                  this.overlayElement.style.display = 'none';
                }
                this.scrollHijacked = true;
                this.dragEnabled = true;
              } else {
                // Not fully in view - show overlay, block interactions
                if (this.overlayElement) {
                  this.overlayElement.style.display = 'block';
                }
                this.scrollHijacked = false;
                this.dragEnabled = false;

                // Reset to first slide when scrolling away
                if (this.splideInstance && this.currentSlideIndex !== 0) {
                  this.splideInstance.go(0);
                }
              }
            });
          }, {
            threshold: [0, 0.25, 0.5, 0.75, 0.95, 1.0],
            rootMargin: '0px'
          });

          observer.observe(this.slideshowElement);

          // Listen to wheel/touch events for scroll hijacking
          const handleWheel = (e) => {
            if (!this.scrollHijacked || !this.splideInstance) return;

            // Prevent default scroll behavior
            e.preventDefault();
            e.stopPropagation();

            // Accumulate scroll delta for smoother control
            this.accumulatedDelta += e.deltaY;

            // Threshold for triggering slide change (adjust for sensitivity)
            const threshold = 100;

            if (Math.abs(this.accumulatedDelta) >= threshold) {
              if (this.accumulatedDelta > 0) {
                // Scrolling down - go to next slide
                if (this.currentSlideIndex < this.totalSlides - 1) {
                  this.splideInstance.go('>');
                  this.accumulatedDelta = 0;
                } else {
                  // On last slide, restore overlay and release scroll hijacking
                  if (this.overlayElement) {
                    this.overlayElement.style.display = 'block';
                  }
                  this.scrollHijacked = false;
                  this.dragEnabled = false;
                  this.accumulatedDelta = 0;
                  // Allow normal scroll to continue
                  window.scrollBy({ top: 100, behavior: 'smooth' });
                }
              } else {
                // Scrolling up - go to previous slide
                if (this.currentSlideIndex > 0) {
                  this.splideInstance.go('<');
                  this.accumulatedDelta = 0;
                } else {
                  // On first slide scrolling up, restore overlay and release hijacking
                  if (this.overlayElement) {
                    this.overlayElement.style.display = 'block';
                  }
                  this.scrollHijacked = false;
                  this.dragEnabled = false;
                  this.accumulatedDelta = 0;
                }
              }
            }
          };

          let touchStartY = 0;
          let touchCurrentY = 0;

          const handleTouchStart = (e) => {
            if (!this.scrollHijacked) return;
            touchStartY = e.touches[0].clientY;
            touchCurrentY = touchStartY;
          };

          const handleTouchMove = (e) => {
            if (!this.scrollHijacked || !this.splideInstance) return;

            e.preventDefault();
            e.stopPropagation();

            touchCurrentY = e.touches[0].clientY;
            const deltaY = touchStartY - touchCurrentY;

            this.accumulatedDelta += deltaY;
            touchStartY = touchCurrentY;

            const threshold = 50;

            if (Math.abs(this.accumulatedDelta) >= threshold) {
              if (this.accumulatedDelta > 0) {
                // Swiping up - go to next slide
                if (this.currentSlideIndex < this.totalSlides - 1) {
                  this.splideInstance.go('>');
                  this.accumulatedDelta = 0;
                } else {
                  // On last slide, restore overlay and release scroll hijacking
                  if (this.overlayElement) {
                    this.overlayElement.style.display = 'block';
                  }
                  this.scrollHijacked = false;
                  this.dragEnabled = false;
                  this.accumulatedDelta = 0;
                }
              } else {
                // Swiping down - go to previous slide
                if (this.currentSlideIndex > 0) {
                  this.splideInstance.go('<');
                  this.accumulatedDelta = 0;
                } else {
                  // On first slide swiping down, restore overlay and release hijacking
                  if (this.overlayElement) {
                    this.overlayElement.style.display = 'block';
                  }
                  this.scrollHijacked = false;
                  this.dragEnabled = false;
                  this.accumulatedDelta = 0;
                }
              }
            }
          };

          const handleTouchEnd = () => {
            this.accumulatedDelta = 0;
          };

          // Add event listeners
          this.slideshowElement.addEventListener('wheel', handleWheel, { passive: false });
          this.slideshowElement.addEventListener('touchstart', handleTouchStart, { passive: true });
          this.slideshowElement.addEventListener('touchmove', handleTouchMove, { passive: false });
          this.slideshowElement.addEventListener('touchend', handleTouchEnd, { passive: true });
        },

        slideHandleMouseDown(e, touch) {
          if (!this.dragEnabled) return;
          if (this.slideIndex != '') {
            this.scrollTopBody = document.documentElement.scrollTop;
            this.slideOnMouseDown = true;
            if (touch) {
              this.slideOfset = e.targetTouches[0].pageY;
            } else {
              this.slideOfset = e.pageY;
            }
          }
        },
        slideHandleMouseMove(e, touch) {
          if (!this.dragEnabled) return;
          if(!this.slideOnMouseDown) return;
          e.preventDefault();
          if (this.slideIndex != '') {
            let spacing = 0;
            if (touch) {
              spacing = this.slideOfset - e.targetTouches[0].pageY;
            } else {
              spacing = this.slideOfset - e.pageY;
            }
            if (this.slideIndex == 'first' && spacing > -50) return;
            if (this.slideIndex == 'last' && spacing < 50) return;
            let spacingMove = this.scrollTopBody + spacing * 2;
            window.scrollTo({top: spacingMove , behavior: 'smooth'});
          }
        },
        slideHandleMouseLeave() {
          this._slideRemoveGrabing();
        },
        slideHandleMouseUp() {
          this._slideRemoveGrabing();
        },
        _slideRemoveGrabing() {
          this.slideOnMouseDown = false;
        },
      }))
    });
  });
} 